/**
 * The change planner's presentation rules (docs/plans/change-planner.md §6): labels and colours per
 * status and channel, the chat's cards and their values (`document:open:<id>` for a request,
 * backend/plans/document/chat-card.ts; `plan:<action>:<id>` for the plan a request became, built here
 * once the plan is made, since that happens after the reply), and what disables a control. Every
 * classification (readiness, missing inputs, schedulability) comes from the backend.
 */
import type { plans } from './client';
import type { InteractiveMode, PlanCardSummary } from '@/types';

export const PLAN_VALUE_PREFIX = 'plan:';

export type PlanCardAction = { action: 'approve' | 'open'; planId: string; watch?: boolean };

/**
 * The action a chat card option carries, or null for every other value. `plan:approve:<id>:watch` and
 * `…:nowatch` carry the answer to "Watch the browser?" (D19) when the chat asked it.
 */
export function planCardAction(value: string): PlanCardAction | null {
  if (!value.startsWith(PLAN_VALUE_PREFIX)) return null;
  const [action, planId, answer] = value.slice(PLAN_VALUE_PREFIX.length).split(':');
  if ((action !== 'approve' && action !== 'open') || !planId || planId.trim() === '') return null;
  if (answer !== undefined && answer !== 'watch' && answer !== 'nowatch') return null;
  return { action, planId: planId.trim(), ...(answer === undefined ? {} : { watch: answer === 'watch' }) };
}

/** The chat's "Watch the browser?" card for a plan with browser steps (D19). */
export function watchBrowserCard(planId: string) {
  return {
    type: 'choice' as const,
    prompt: 'Some steps run in your browser. Watch ZAC drive, or just have it done?',
    options: [
      { id: `plan-watch-${planId}`, label: 'Show me in the browser', value: `${PLAN_VALUE_PREFIX}approve:${planId}:watch`, icon: 'watch' as const },
      { id: `plan-nowatch-${planId}`, label: 'Just do it', value: `${PLAN_VALUE_PREFIX}approve:${planId}:nowatch`, icon: 'quick' as const },
    ],
  };
}

/** A request's card: `document:open:<id>` opens its page. The same prefix as backend/plans/document/chat-card.ts. */
export const DOCUMENT_VALUE_PREFIX = 'document:';

/** The card for a request being read, as the backend's `documentCard` builds it (a file attached in the chat). */
export function documentCardOf(id: string, title: string): InteractiveMode {
  return {
    type: 'choice',
    prompt: `Reading: ${title}`,
    options: [{ id: `document-open-${id}`, label: 'Open', description: 'Follow it in the Changes tab', value: `${DOCUMENT_VALUE_PREFIX}open:${id}`, icon: 'plan' }],
    document: { id, title },
  };
}

/** The request a card option opens, or null for every other value. */
export function documentCardTarget(value: string): string | null {
  if (!value.startsWith(`${DOCUMENT_VALUE_PREFIX}open:`)) return null;
  const id = value.slice(`${DOCUMENT_VALUE_PREFIX}open:`.length).trim();
  return id === '' ? null : id;
}

/** A plan as the chat's card shows it: its steps, each with its channel and what it still needs. */
export function planSummaryOf(plan: plans.PlanView): PlanCardSummary {
  const blocker = plan.readiness.blockers.find((b) => b.code !== 'write_access');
  return {
    id: plan.id,
    version: plan.version,
    title: plan.title,
    steps: plan.steps.map((step) => ({ key: step.key, title: step.title, channel: step.channel, missingInputs: step.missing_inputs, missingScopes: step.missing_scopes })),
    hasBrowserSteps: plan.steps.some((step) => step.channel === 'browser'),
    canApprove: plan.readiness.ok,
    ...(blocker ? { blockedReason: blocker.detail } : {}),
  };
}

/**
 * The card a request becomes once its plan is made (D11): Approve & run and Open plan, or the write-access bridge
 * when the connection is read-only (the readiness says so). Nothing runs until the user approves.
 */
export function planCardOf(plan: plans.PlanView): InteractiveMode {
  const summary = planSummaryOf(plan);
  const open = { id: `plan-open-${plan.id}`, label: 'Open plan', description: 'Review and edit it in the Changes tab', value: `${PLAN_VALUE_PREFIX}open:${plan.id}`, icon: 'plan' as const };
  const steps = `${summary.steps.length} step${summary.steps.length === 1 ? '' : 's'}`;
  if (summary.steps.some((step) => step.channel !== 'human') && needsWriteAccess(plan)) {
    return {
      type: 'choice',
      prompt: `Plan: ${summary.title}, ${steps}. To run it I need write access to your Zoom account; I'll add the scopes (you'll see it in the browser) and bring you back to the plan.`,
      options: [{ id: 'enable-write', label: 'Enable write access', description: 'Add the write scopes, then come back to the plan', value: 'enable-write', icon: 'confirm' }, open, { id: 'cancel', label: 'Not now', value: 'cancel', icon: 'cancel' }],
      plan: summary,
    };
  }
  const approve = {
    id: `plan-approve-${plan.id}`, label: 'Approve & run', description: 'Run every step now', value: `${PLAN_VALUE_PREFIX}approve:${plan.id}`, icon: 'confirm' as const,
    ...(summary.canApprove ? { highlight: true } : { disabled: true, hint: summary.blockedReason ?? 'the plan is not ready' }),
  };
  return {
    type: 'choice',
    prompt: summary.canApprove ? `Plan: ${summary.title}, ${steps}. Nothing changes until you approve it.` : `Plan: ${summary.title}, ${steps}. Before it can run: ${summary.blockedReason ?? 'it needs a few details'}.`,
    options: [approve, open],
    plan: summary,
  };
}

export const STATUS_LABELS: Record<plans.PlanStatus, string> = {
  draft: 'Draft', ready: 'Ready', approved: 'Approved', scheduled: 'Scheduled', running: 'Running', completed: 'Completed', failed: 'Failed',
  reverting: 'Reverting', reverted: 'Reverted', revert_partial: 'Partly reverted', cancelled: 'Cancelled',
};

export const STATUS_STYLES: Record<plans.PlanStatus, string> = {
  draft: 'bg-secondary text-muted-foreground', ready: 'bg-blue-500/15 text-blue-400', approved: 'bg-indigo-500/15 text-indigo-400',
  scheduled: 'bg-amber-500/15 text-amber-400', running: 'bg-blue-500/15 text-blue-400 animate-pulse', completed: 'bg-emerald-500/15 text-emerald-400',
  failed: 'bg-red-500/15 text-red-400', reverting: 'bg-orange-500/15 text-orange-400 animate-pulse', reverted: 'bg-orange-500/15 text-orange-400',
  revert_partial: 'bg-orange-500/15 text-orange-400', cancelled: 'bg-secondary text-muted-foreground',
};

export const CHANNEL_LABELS: Record<plans.Channel, string> = { api: 'API', browser: 'Browser', human: 'You' };

export const CHANNEL_STYLES: Record<plans.Channel, string> = {
  api: 'bg-blue-500/15 text-blue-400', browser: 'bg-purple-500/15 text-purple-400', human: 'bg-orange-500/15 text-orange-400',
};

export const STEP_STATUS_STYLES: Record<plans.StepRunStatus, string> = {
  pending: 'border-border', running: 'border-blue-500 ring-2 ring-blue-500/30', waiting_human: 'border-orange-500 ring-2 ring-orange-500/30',
  completed: 'border-emerald-500', failed: 'border-red-500', skipped: 'border-border opacity-50',
};

export const STEP_RUN_STATUS_TEXT: Record<plans.StepRunStatus, string> = {
  pending: 'pending', running: 'running', waiting_human: 'waiting for you', completed: 'completed', failed: 'failed', skipped: 'skipped',
};

/** A completed step's revert, once the run reverted: what the node and the timeline say of it. */
export const REVERT_STATUS_TEXT: Record<plans.RevertStatus, string> = { pending: 'reverting…', completed: 'reverted', failed: 'revert failed', manual: 'undo by hand' };

export const REVERT_NODE_STYLES: Record<plans.RevertStatus, string> = {
  pending: 'border-orange-500 ring-2 ring-orange-500/30', completed: 'border-orange-500', failed: 'border-red-500 border-dashed', manual: 'border-orange-500 border-dashed',
};

/** The status chip's word; a run the user stopped reads "Stopped", not "Failed". */
export function statusLabel(plan: Pick<plans.PlanView, 'status' | 'run'>): string {
  if (plan.status === 'failed' && plan.run?.failure?.code === 'stopped') return 'Stopped';
  return STATUS_LABELS[plan.status];
}

export type OutcomeTone = 'good' | 'bad' | 'undone' | 'muted';

/** How the outcome line reads: green for done, red for failed, orange for reverted, grey for cancelled. */
export function outcomeTone(status: plans.PlanStatus): OutcomeTone | null {
  switch (status) {
    case 'completed': return 'good';
    case 'failed': return 'bad';
    case 'reverted': case 'revert_partial': return 'undone';
    case 'cancelled': return 'muted';
    default: return null;
  }
}

export const OUTCOME_TONE_STYLES: Record<OutcomeTone, string> = {
  good: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200',
  bad: 'border-red-500/30 bg-red-500/10 text-red-200',
  undone: 'border-orange-500/30 bg-orange-500/10 text-orange-200',
  muted: 'border-border bg-secondary/40 text-muted-foreground',
};

/** Whether the plan waits on write access (D3): then the primary action is enabling it, not approving. */
export function needsWriteAccess(plan: Pick<plans.PlanView, 'readiness'>): boolean {
  return plan.readiness.blockers.some((blocker) => blocker.code === 'write_access');
}

/** Why Approve is disabled, or null: the first blocker, in the backend's words. */
export function approveDisabledReason(plan: Pick<plans.PlanView, 'status' | 'readiness'>): string | null {
  if (plan.status !== 'draft' && plan.status !== 'ready') return `a ${STATUS_LABELS[plan.status].toLowerCase()} plan cannot be approved`;
  if (plan.readiness.ok) return null;
  return plan.readiness.blockers[0]?.detail ?? 'the plan is not ready';
}

export function describeWhen(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return `${Math.max(diffSec, 0)}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return date.toLocaleDateString();
}

/** A Zoom scope as an admin reads it: "phone:write:call_queue_policy:admin" -> "Phone: change call queue policy". */
export function scopeLabel(scope: string): string {
  const [product = '', access = '', resource = ''] = scope.split(':');
  const verb = access === 'write' ? 'change' : access === 'read' ? 'see' : access;
  const what = resource.replace(/^list_/, '').replace(/_/g, ' ');
  const name = product.replace(/_/g, ' ');
  return `${name.charAt(0).toUpperCase()}${name.slice(1)}: ${verb} ${what}`.trim();
}

/**
 * The "Before approving" lines in the admin's words: each step by its title (not its key), and the scopes the app
 * lacks gathered in one line per step, each scope said plainly.
 */
export function blockerLines(plan: Pick<plans.PlanView, 'readiness' | 'steps'>): string[] {
  const titles = new Map(plan.steps.map((step) => [step.key, step.title]));
  const titleOf = (key: string): string => titles.get(key) ?? key;
  return plan.readiness.blockers.map((b) => {
    if (b.step_key === undefined) return b.detail;
    if (b.code === 'missing_scopes') {
      const scopes = (b.names ?? []).map(scopeLabel);
      const products = [...new Set(scopes.map((s) => s.split(':')[0]))].join(', ');
      return `${titleOf(b.step_key)}: your Zoom app can't ${scopes.map((s) => s.replace(/^[^:]+: /, '')).join(', ')} yet (${products})`;
    }
    if (b.code === 'missing_inputs') return `${titleOf(b.step_key)}: needs a value for ${(b.names ?? []).map(fieldLabel).join(', ')}`;
    return b.detail.replace(`step "${b.step_key}"`, titleOf(b.step_key));
  });
}

/** A field's name as an admin reads it: "siteId" or "site_id" -> "site id", "a.b.timeout" -> "timeout". */
export function fieldLabel(field: string): string {
  return field.split('.').pop()!.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim().toLowerCase();
}
